import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ClipboardList, Plus, Users, CheckCircle, Clock, AlertCircle,
  ChevronDown, ChevronUp, Trash2, X, Play, Pause, School
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_OPTIONS = [
  { key: 'listening', label: 'Listening' },
  { key: 'speaking', label: 'Speaking' },
  { key: 'reading', label: 'Reading' },
  { key: 'writing', label: 'Writing' },
];

const GRADE_OPTIONS = ['K', '1', '2', '3-4', '5-6', '7-8', '9-12'];

const AssignmentManager = ({ classes = [], schools = [], level = 'teacher' }) => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailData, setDetailData] = useState({});
  const [creating, setCreating] = useState(false);
  // v6.32.106 — Individual-Student assignment picker. Pre-fix the
  // "Individual Students" toggle had no UI body; the dropdown of
  // students never rendered, so teachers couldn't assign per-student.
  // We hydrate students lazily by fetching each of the teacher's
  // classes once when the user picks the Individual Students mode.
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);
  // v6.32.107 — track whether we've already attempted the load so a
  // failed call does not infinite-loop the useEffect (students.length
  // stays 0 → effect re-runs → fails again → forever).
  const studentsFetchedRef = useRef(false);

  // Form state
  const [form, setForm] = useState({
    title: '',
    assessment_type: 'benchmark',
    assignment_level: level === 'district' ? 'district' : 'class',
    school_ids: [],
    class_ids: [],
    student_ids: [],
    grade_levels: [],
    modalities: ['listening', 'speaking', 'reading', 'writing'],
    form_id: 'A',
    due_date: '',
    notes: '',
  });

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/assignments`);
      setAssignments(res.data.assignments || []);
    } catch {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAssignments(); }, []);

  const toggleModality = (mod) => {
    setForm(prev => ({
      ...prev,
      modalities: prev.modalities.includes(mod)
        ? prev.modalities.filter(m => m !== mod)
        : [...prev.modalities, mod],
    }));
  };

  const toggleGrade = (g) => {
    setForm(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.includes(g)
        ? prev.grade_levels.filter(x => x !== g)
        : [...prev.grade_levels, g],
    }));
  };

  const toggleSchool = (sid) => {
    setForm(prev => ({
      ...prev,
      school_ids: prev.school_ids.includes(sid)
        ? prev.school_ids.filter(x => x !== sid)
        : [...prev.school_ids, sid],
    }));
  };

  const toggleClass = (cid) => {
    setForm(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(cid)
        ? prev.class_ids.filter(x => x !== cid)
        : [...prev.class_ids, cid],
    }));
  };

  // v6.32.106 — toggle a student in/out of form.student_ids
  const toggleStudent = (sid) => {
    setForm(prev => ({
      ...prev,
      student_ids: prev.student_ids.includes(sid)
        ? prev.student_ids.filter(x => x !== sid)
        : [...prev.student_ids, sid],
    }));
  };

  // v6.32.107 — hydrate students lazily when the user switches to the
  // Individual Students mode. Single-call to GET /classes/my-students
  // replaces the prior N-class fanout. Hardened over v6.32.106:
  //   - useRef sentinel prevents infinite retry on failure
  //   - 15s axios timeout surfaces network hangs as a clear error
  //   - explicit retry button + console.error for debuggability
  useEffect(() => {
    if (form.assignment_level !== 'student') return;
    if (studentsFetchedRef.current) return;
    studentsFetchedRef.current = true;
    let cancelled = false;
    const load = async () => {
      setStudentsLoading(true);
      setStudentsError(null);
      try {
        const res = await axios.get(`${API}/classes/my-students`, { timeout: 15000 });
        if (cancelled) return;
        setStudents(res.data.students || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AssignmentManager] /classes/my-students failed', err);
        if (!cancelled) {
          setStudentsError(err?.message || 'Failed to load students');
          toast.error('Failed to load students');
        }
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [form.assignment_level]);

  const retryLoadStudents = () => {
    studentsFetchedRef.current = false;
    setStudentsError(null);
    setStudents([]);
    // Toggling assignment_level isn't necessary; just trigger the
    // effect by flipping the ref + bumping a state. Simpler: directly
    // re-run the loader here.
    setStudentsLoading(true);
    axios.get(`${API}/classes/my-students`, { timeout: 15000 })
      .then(res => {
        setStudents(res.data.students || []);
        studentsFetchedRef.current = true;
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('[AssignmentManager] retry failed', err);
        setStudentsError(err?.message || 'Failed to load students');
        toast.error('Failed to load students');
      })
      .finally(() => setStudentsLoading(false));
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.grade_levels.length === 0) { toast.error('Select at least one grade level'); return; }
    if (form.modalities.length === 0) { toast.error('Select at least one modality'); return; }
    if (form.assignment_level === 'class' && form.class_ids.length === 0) {
      toast.error('Select at least one class'); return;
    }

    setCreating(true);
    try {
      const payload = { ...form };
      if (payload.assignment_level === 'district' && payload.school_ids.length === 0) {
        delete payload.school_ids; // all schools
      }
      await axios.post(`${API}/assignments`, payload);
      toast.success('Assessment assigned successfully');
      setShowCreate(false);
      setForm({
        title: '', assessment_type: 'benchmark', assignment_level: level === 'district' ? 'district' : 'class',
        school_ids: [], class_ids: [], student_ids: [],
        grade_levels: [], modalities: ['listening', 'speaking', 'reading', 'writing'],
        form_id: 'A', due_date: '', notes: '',
      });
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setCreating(false);
    }
  };

  const loadDetail = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    try {
      const res = await axios.get(`${API}/assignments/${id}`);
      setDetailData(prev => ({ ...prev, [id]: res.data }));
      setExpandedId(id);
    } catch {
      toast.error('Failed to load details');
    }
  };

  const closeAssignment = async (id) => {
    try {
      await axios.patch(`${API}/assignments/${id}`, { status: 'closed' });
      toast.success('Assignment closed');
      fetchAssignments();
    } catch {
      toast.error('Failed to close assignment');
    }
  };

  const deleteAssignment = async (id) => {
    try {
      await axios.delete(`${API}/assignments/${id}`);
      toast.success('Assignment deleted');
      fetchAssignments();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'completed' || status === 'scored') return <CheckCircle size={14} className="text-green-600" />;
    if (status === 'in_progress' || status === 'paused') return <Clock size={14} className="text-amber-600" />;
    return <AlertCircle size={14} className="text-gray-400" />;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="loading-spinner"></div></div>;
  }

  return (
    <div data-testid="assignment-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assessment Assignments
          </h2>
          <p className="text-sm text-gray-500">
            {level === 'district'
              ? 'Administer assessments across schools in your district'
              : 'Assign assessments to your classes or individual students'}
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(!showCreate)}
          className="btn-sharp-primary h-10"
          data-testid="create-assignment-btn"
        >
          {showCreate ? <><X size={16} className="mr-2" /> Cancel</> : <><Plus size={16} className="mr-2" /> New Assignment</>}
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="border-2 border-[#002FA7] p-6 mb-8 space-y-5" data-testid="create-assignment-form">
          <div>
            <Label className="text-sm font-semibold mb-1 block">Assignment Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Spring 2026 WIDA ACCESS"
              className="h-11 border-2 border-gray-300 rounded-none"
              data-testid="assignment-title-input"
            />
          </div>

          {/* v6.32.180 — Assessment Type selector (Benchmark / Screener / Practice) */}
          <div data-testid="assessment-type-picker">
            <Label className="text-sm font-semibold mb-2 block">Assessment Type *</Label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'benchmark', label: 'Full Benchmark', desc: '~45 min — all 4 modalities' },
                { value: 'screener',  label: 'Adaptive Screener', desc: '~10 min — quick placement' },
                { value: 'practice',  label: 'Practice', desc: 'Untimed, no scoring' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, assessment_type: opt.value }))}
                  className={`px-4 py-2 border-2 text-sm font-semibold transition-colors text-left ${
                    form.assessment_type === opt.value
                      ? 'bg-[#002FA7] text-white border-[#002FA7]'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]'
                  }`}
                  data-testid={`assessment-type-${opt.value}-btn`}
                  aria-pressed={form.assessment_type === opt.value}
                >
                  <div>{opt.label}</div>
                  <div className={`text-xs font-normal ${form.assessment_type === opt.value ? 'text-blue-100' : 'text-gray-500'}`}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Level selector (district admin only) */}
          {level === 'district' && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Assignment Scope *</Label>
              <div className="flex gap-2">
                {[
                  { value: 'district', label: 'Entire District' },
                  { value: 'school', label: 'Select Schools' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, assignment_level: opt.value }))}
                    className={`px-4 py-2 border-2 text-sm font-semibold transition-colors ${
                      form.assignment_level === opt.value
                        ? 'bg-[#002FA7] text-white border-[#002FA7]'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'
                    }`}
                    data-testid={`scope-${opt.value}-btn`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* School selector (when scope is school) */}
          {form.assignment_level === 'school' && schools.length > 0 && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Select Schools *</Label>
              <div className="flex flex-wrap gap-2">
                {schools.map(s => (
                  <button
                    key={s.school_id}
                    type="button"
                    onClick={() => toggleSchool(s.school_id)}
                    className={`px-3 py-1.5 border-2 text-xs font-semibold transition-colors ${
                      form.school_ids.includes(s.school_id)
                        ? 'bg-[#002FA7] text-white border-[#002FA7]'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'
                    }`}
                    data-testid={`school-${s.school_id}-btn`}
                  >
                    <School size={12} className="inline mr-1" />
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Teacher-level: class or student */}
          {level === 'teacher' && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Assign To *</Label>
              <div className="flex gap-2 mb-3">
                {[
                  { value: 'class', label: 'Whole Class' },
                  { value: 'student', label: 'Individual Students' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, assignment_level: opt.value }))}
                    className={`px-4 py-2 border-2 text-sm font-semibold transition-colors ${
                      form.assignment_level === opt.value
                        ? 'bg-[#002FA7] text-white border-[#002FA7]'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'
                    }`}
                    data-testid={`scope-${opt.value}-btn`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.assignment_level === 'class' && classes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {classes.map(c => (
                    <button
                      key={c.class_id}
                      type="button"
                      onClick={() => toggleClass(c.class_id)}
                      className={`px-3 py-1.5 border-2 text-xs font-semibold transition-colors ${
                        form.class_ids.includes(c.class_id)
                          ? 'bg-[#002FA7] text-white border-[#002FA7]'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'
                      }`}
                      data-testid={`class-${c.class_id}-btn`}
                    >
                      {c.name} ({c.grade_level})
                    </button>
                  ))}
                </div>
              )}
              {/* v6.32.106 — Individual-student picker (was missing pre-fix) */}
              {form.assignment_level === 'student' && (
                <div data-testid="assignment-student-picker">
                  {studentsLoading && (
                    <div className="text-xs text-gray-500" data-testid="assignment-students-loading">
                      Loading students…
                    </div>
                  )}
                  {!studentsLoading && studentsError && (
                    <div className="text-xs text-red-600 flex items-center gap-2" data-testid="assignment-students-error">
                      <span>Couldn't load students: {studentsError}</span>
                      <button
                        type="button"
                        onClick={retryLoadStudents}
                        className="px-2 py-0.5 border border-red-300 text-red-700 hover:bg-red-50 font-semibold"
                        data-testid="assignment-students-retry-btn"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {!studentsLoading && !studentsError && students.length === 0 && (
                    <div className="text-xs text-gray-500 italic" data-testid="assignment-students-empty">
                      No students enrolled in any of your classes yet. Add students to a class first, then return here.
                    </div>
                  )}
                  {!studentsLoading && !studentsError && students.length > 0 && (
                    <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto pr-1">
                      {students.map(s => (
                        <button
                          key={s.user_id}
                          type="button"
                          onClick={() => toggleStudent(s.user_id)}
                          className={`px-3 py-1.5 border-2 text-xs font-semibold transition-colors text-left ${
                            form.student_ids.includes(s.user_id)
                              ? 'bg-[#002FA7] text-white border-[#002FA7]'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]'
                          }`}
                          data-testid={`student-${s.user_id}-btn`}
                          title={s.email || ''}
                        >
                          <span className="block">{s.name}</span>
                          <span className={`block text-[10px] font-normal ${form.student_ids.includes(s.user_id) ? 'text-blue-100' : 'text-gray-500'}`}>
                            {s.class_name}{s.grade_level ? ` · Grade ${s.grade_level}` : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {form.student_ids.length > 0 && (
                    <div className="mt-2 text-[11px] text-gray-500" data-testid="assignment-students-count">
                      {form.student_ids.length} student{form.student_ids.length === 1 ? '' : 's'} selected
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Grade levels */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">Grade Levels *</Label>
            <div className="flex flex-wrap gap-2">
              {GRADE_OPTIONS.map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGrade(g)}
                  className={`px-3 py-1.5 border-2 text-xs font-semibold transition-colors ${
                    form.grade_levels.includes(g)
                      ? 'bg-[#002FA7] text-white border-[#002FA7]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'
                  }`}
                  data-testid={`grade-${g}-btn`}
                >
                  {g === 'K' ? 'K' : `Grade ${g}`}
                </button>
              ))}
            </div>
          </div>

          {/* Modalities */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">Modalities</Label>
            <div className="flex gap-2">
              {MODALITY_OPTIONS.map(m => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggleModality(m.key)}
                  className={`px-3 py-1.5 border-2 text-xs font-semibold transition-colors ${
                    form.modalities.includes(m.key)
                      ? 'bg-[#002FA7] text-white border-[#002FA7]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]'
                  }`}
                  data-testid={`modality-${m.key}-btn`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Due date + Form */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold mb-1 block">Due Date</Label>
              <Input
                type="date"
                value={form.due_date ? form.due_date.split('T')[0] : ''}
                onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value ? e.target.value + 'T23:59:59Z' : '' }))}
                className="h-11 border-2 border-gray-300 rounded-none"
                data-testid="assignment-due-date"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Test Form</Label>
              <div className="flex gap-2">
                {['A', 'B', 'C'].map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, form_id: f }))}
                    className={`px-4 py-2 border-2 text-sm font-semibold ${
                      form.form_id === f
                        ? 'bg-[#002FA7] text-white border-[#002FA7]'
                        : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    Form {f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-semibold mb-1 block">Notes (optional)</Label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Instructions or context for this assignment..."
              className="w-full h-20 p-3 border-2 border-gray-300 text-sm resize-none focus:border-[#002FA7] outline-none"
              data-testid="assignment-notes"
            />
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating}
            className="btn-sharp-primary h-12 px-8"
            data-testid="submit-assignment-btn"
          >
            {creating ? 'Creating...' : 'Assign Assessment'}
          </Button>
        </div>
      )}

      {/* Assignment List */}
      {assignments.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200" data-testid="no-assignments">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <h3 className="font-semibold text-gray-500 mb-1">No Assignments Yet</h3>
          <p className="text-sm text-gray-400">
            Create your first assessment assignment to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="assignment-list">
          {assignments.map(a => {
            const isExpanded = expandedId === a.id;
            const detail = detailData[a.id];
            const completionPct = a.total_students > 0
              ? Math.min(100, Math.round((a.completed_count / a.total_students) * 100))
              : 0;
            const isOverdue = a.due_date && new Date(a.due_date) < new Date() && a.status === 'active';

            return (
              <div
                key={a.id}
                className={`border-2 ${a.status === 'closed' ? 'border-gray-200 opacity-60' : isOverdue ? 'border-red-300' : 'border-gray-200'}`}
                data-testid={`assignment-card-${a.id}`}
              >
                {/* Card header */}
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => loadDetail(a.id)}
                >
                  <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
                    a.assignment_level === 'district' ? 'bg-indigo-100 text-indigo-700'
                    : a.assignment_level === 'school' ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
                  }`}>
                    {a.assignment_level === 'district' ? <School size={20} /> : <Users size={20} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{a.title}</span>
                      <span className={`text-xs px-2 py-0.5 font-medium ${
                        a.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {a.status}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 capitalize">
                        {a.assignment_level}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Grades: {a.grade_levels?.join(', ')} | {a.total_students} students
                      {a.due_date && ` | Due: ${new Date(a.due_date).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {/* Progress */}
                    <div className="text-right w-24">
                      <div className="text-sm font-bold">{completionPct}%</div>
                      <div className="w-24 h-1.5 bg-gray-200 mt-1">
                        <div className="h-full bg-green-500 transition-all" style={{ width: `${completionPct}%` }} />
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{a.completed_count}/{a.total_students}</div>
                    </div>
                    {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && detail && (
                  <div className="border-t border-gray-200 p-4" data-testid={`assignment-detail-${a.id}`}>
                    {/* Status summary */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-green-50 border border-green-200 p-3 text-center">
                        <div className="text-lg font-bold text-green-700">{a.completed_count}</div>
                        <div className="text-xs text-green-600">Completed</div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 p-3 text-center">
                        <div className="text-lg font-bold text-amber-700">{a.in_progress_count}</div>
                        <div className="text-xs text-amber-600">In Progress</div>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 p-3 text-center">
                        <div className="text-lg font-bold text-gray-600">{a.not_started_count}</div>
                        <div className="text-xs text-gray-500">Not Started</div>
                      </div>
                    </div>

                    {a.notes && (
                      <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 p-3 mb-4">{a.notes}</p>
                    )}

                    {/* Student list */}
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Student Status</div>
                    <div className="max-h-64 overflow-y-auto border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-semibold text-gray-600">Student</th>
                            <th className="text-left p-2 font-semibold text-gray-600">Status</th>
                            <th className="text-left p-2 font-semibold text-gray-600">Started</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.student_statuses?.map(s => (
                            <tr key={s.user_id} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="p-2">
                                <div className="font-medium">{s.name}</div>
                                <div className="text-xs text-gray-400">{s.email}</div>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-1.5">
                                  {getStatusIcon(s.status)}
                                  <span className="capitalize text-xs">{s.status.replace('_', ' ')}</span>
                                </div>
                              </td>
                              <td className="p-2 text-xs text-gray-500">
                                {s.started_at ? new Date(s.started_at).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      {a.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); closeAssignment(a.id); }}
                          className="btn-sharp h-8 text-xs"
                          data-testid={`close-assignment-${a.id}`}
                        >
                          <Pause size={14} className="mr-1" /> Close Assignment
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); deleteAssignment(a.id); }}
                        className="btn-sharp h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-assignment-${a.id}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignmentManager;
